/**
 * The entire implementation of Q1 part A option A: the pool settings screen
 * lives at `?tab=settings&panel=<slug>` on the existing `/pools` route. It
 * used to be `?view=settings`, and `aliasLegacyPoolView` keeps every link
 * that still carries that address resolving here (ISSUE-952).
 *
 * Q1 is unanswered as of this writing; this module ships the recommendation
 * under the team's standing silence rule: silence on an open decision means
 * the recommendation ships. Every
 * component and the `/pools` route read the settings URL state through the
 * four functions below, never through `search.view` or `search.panel`
 * directly, so switching to option B (a dedicated route) or option C (a
 * full-screen sheet) later means rewriting this file, the two lines it adds
 * to `apps/app/src/routeState/sectionSearch.ts`, and the branch in
 * `apps/app/src/routes/pools.tsx`. Nothing else.
 */
import {
  openPoolTabSearch,
  readPoolTab,
  type typePoolTab,
} from "../poolScreen/poolScreenRoute";

export const POOL_SETTINGS_TAB: typePoolTab = "settings";

export const POOL_SETTINGS_PANELS = [
  "pool",
  "joining",
  "contest-rules",
  "members",
  "contests",
  "season",
  "awards",
  "chat",
  "notifications",
  "danger-zone",
] as const;

export type typePoolSettingsPanel = (typeof POOL_SETTINGS_PANELS)[number];

export function isPoolSettingsOpen(search: {
  poolId?: string | undefined;
  tab?: string | undefined;
}): boolean {
  return (
    readPoolTab(search) === POOL_SETTINGS_TAB &&
    typeof search.poolId === "string" &&
    search.poolId !== ""
  );
}

export function readPoolSettingsPanel(search: {
  panel?: string | undefined;
}): typePoolSettingsPanel | null {
  const panel = search.panel;
  if (panel === undefined) return null;
  return (POOL_SETTINGS_PANELS as readonly string[]).includes(panel)
    ? (panel as typePoolSettingsPanel)
    : null;
}

export function openPoolSettingsSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
  options?: { panel?: typePoolSettingsPanel },
): TSearch & {
  tab: typePoolTab;
  panel?: typePoolSettingsPanel;
} {
  const next: Record<string, unknown> = openPoolTabSearch(
    search,
    POOL_SETTINGS_TAB,
  );
  if (options?.panel !== undefined) {
    next.panel = options.panel;
  }
  return next as TSearch & {
    tab: typePoolTab;
    panel?: typePoolSettingsPanel;
  };
}

export function closePoolSettingsSearch<
  TSearch extends Record<string, unknown>,
>(search: TSearch): TSearch {
  const dropped = new Set<string>(["view", "tab", "panel"]);
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as TSearch;
}
